import * as d3 from 'd3'
import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'

import locale from 'live/init/global-locale'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { CustomList } from 'plugin-codaf-risk-ext-bowtie/generic'
import { DraExportSingleData } from 'plugin-codaf-risk-ext-bowtie/services/exportSingleData'

import { listBarrierTypeIcons, listBarrierTypes } from '../../../services'
import { IReduxActions } from '../../../store'
import {
    IReduxStoreReducers,
    IBowtie,
    IAlarpThreshold,
    IBarrierTypeIcon,
    IBarrierType,
    ICustomZoomLevels
} from '../../../types'
import { d3PromiseSelect, getZoomLevel } from '../../../utils'
import {
    topEventRadius,
    initialThreat,
    initialConsequence,
    threatConsequenceHeight,
    barrierHeight,
    hazardBoxWidth,
    shadowOpacity,
    THREAT_CONSEQUENCE_GAP_Y
} from '../../../utils/constants'
import { BowtieView } from '../../BowtieViews'
import {
    MiddlePulse,
    ThreatAndConsequence,
    TopBar,
    BarrierDetailsModal,
    ActionButtons,
    HelpModal,
    SearchModal
} from './components'
import { BowTieStyledContainer, PdfTableContainer } from './styles'

interface Props {
    width: number
    height: number
    bowtieData: IBowtie
    onCloseBowtie: () => void
    event: BowtieView
    extraInfo: string
    alarpThresholds: IAlarpThreshold[]
    customZoomLevels: ICustomZoomLevels
}

interface IVector3 {
    x: number
    y: number
    k: number
}

export const Bowtie: React.FC<Props> = ({
    height,
    width,
    bowtieData,
    onCloseBowtie,
    extraInfo,
    event,
    alarpThresholds,
    customZoomLevels
}) => {
    const [barrierTypeIconMap] = useState<Map<number, IBarrierTypeIcon>>(new Map())
    const [consequenceX, setConsequenceX] = useState(initialConsequence)
    const [threatX, setThreatX] = useState(initialThreat)
    const [bowtieSVG, setBowtieSVG] = useState<d3.Selection<SVGSVGElement, unknown, HTMLElement, unknown>>(null)
    const [zoomBehavior, setZoomBehavior] = useState<d3.ZoomBehavior<Element, unknown>>(null)
    const [openClose, setOpenClose] = useState<'openAll' | 'closeAll' | 'none'>('none')
    const [openHelpModal, setOpenHelpModal] = useState(false)
    const [openSearchModal, setOpenSearchModal] = useState(false)
    const [highlightInnerCircleRadius, setHighlightInnerCircleRadius] = useState(0)
    const [highlightCircleRadius, setHighlightCircleRadius] = useState(0)
    const [highlightCircleOpacity, setHighlightCircleOpacity] = useState(0)
    const [barrierTypes, setBarrierTypes] = useState<IBarrierType[]>()

    const [translation, setTranslation] = useState<IVector3>({ x: width / 2, y: height / 2, k: 1 })

    const dispatch = useDispatch()
    const {
        consequencesExpandedBarriers,
        threatsExpandedBarriers,
        searchPosition,
        searchDataId
    } = useSelector((state: IReduxStoreReducers) => ({ ...state.bowtieExpandedBarriers, ...state.searchBarrier }))

    const theme = useTheme()

    const generateBowtiePDF = async (): Promise<void> => {
        try {
            await DraExportSingleData({ entityType: 'SINGLE_BOWTIE', id: bowtieData.id })
        } catch (error) {
            if (error.message.toLowerCase().includes('there is at least one')) {
                globalNotification.error(i18n('Confirm incomplete export'))
            } else {
                globalNotification.error(error.message ?? i18n('An error occurred on exporting Bowtie data.'))
            }
        } finally {
        }
    }

    const getHeight = (type: 'threats' | 'consequences'): number => {
        const idx = bowtieData.topEvent[type].length - 1

        const heights = []
        ;(type === 'threats' ? threatsExpandedBarriers : consequencesExpandedBarriers)
            .filter(el => el.barrierId === `${type === 'threats' ? 'thr' : 'cnq'}-${bowtieData.topEvent[type][idx].id}`)
            .forEach(e => e.escalations.forEach(es => heights.push(es.y)))

        const higHeight = heights.sort((a, b) => b - a)[0] ?? 0

        return higHeight
    }

    const getYPosition = (type: 'threat' | 'consequence', index: number): number => {
        const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers

        const arr = type === 'threat' ? bowtieData.topEvent.threats : bowtieData.topEvent.consequences
        const heights = []

        if (index > 0) {
            expandedBarriers
                .filter(el => el.barrierId.startsWith(`${type === 'threat' ? 'thr' : 'cnq'}-${arr[index - 1]?.id}`))
                .forEach(e => e.escalations.forEach(es => heights.push(es.y)))
        }

        const prevThreatsHighHeight = heights.sort((a, b) => b - a)[0]

        return heights.length ? prevThreatsHighHeight + threatConsequenceHeight + THREAT_CONSEQUENCE_GAP_Y : 0
    }

    const drawShadow = (): JSX.Element => {
        const topEventCenter = translation.y * (1 / translation.k)
        const relativeTopEventRadius = topEventRadius * translation.k

        return (
            <g transform={`translate(${translation.x - relativeTopEventRadius}, 0) scale(${translation.k})`}>
                <svg
                    width={hazardBoxWidth}
                    height={topEventCenter > 0 ? topEventCenter : 0}
                    viewBox={`0 0 ${hazardBoxWidth} ${topEventCenter > 0 ? topEventCenter : 0}`}
                    fill="none"
                    opacity={shadowOpacity}
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d={[
                            `M ${hazardBoxWidth / 2} ${topEventCenter}`,
                            `L ${hazardBoxWidth} 0`,
                            `H 0.0205078`,
                            `L ${hazardBoxWidth / 2} ${topEventCenter}`,
                            `Z`
                        ].join(' ')}
                        fill={theme['gray-300']}
                    />
                </svg>
            </g>
        )
    }

    const handleFetchBarrierTypeIconsAndStoreIntoDictionary = async (): Promise<void> => {
        const data = await listBarrierTypeIcons()
        data?.data?.forEach(el => {
            const decodedSvg = atob(el.svgImage)
            el.decodedSvgImage = decodedSvg
            barrierTypeIconMap.set(el.id, el)
        })
    }

    const handleFetchBarrierTypes = async (): Promise<void> => {
        const data = await listBarrierTypes()
        setBarrierTypes(data?.data)
    }

    /** -------------------------------------- ACTION BUTTONS FUNCTIONS -------------------------------------- **/

    const handleZoomOut = useCallback(() => zoomBehavior?.scaleBy(bowtieSVG, 0.67), [bowtieSVG, zoomBehavior])

    const handleZoomIn = useCallback(() => zoomBehavior?.scaleBy(bowtieSVG, 1.5), [bowtieSVG, zoomBehavior])

    const handleCloseHelpModal = useCallback(() => setOpenHelpModal(false), [])

    const handleOpenHelpModal = useCallback(() => setOpenHelpModal(true), [])

    const handleOpenSearchModal = useCallback(() => setOpenSearchModal(true), [])

    const handleResetZoom = useCallback(() => {
        if (bowtieSVG && zoomBehavior) {
            bowtieSVG.call(zoomBehavior.transform, d3.zoomIdentity.translate(width / 2, height / 2 + 30).scale(1))
        }
    }, [bowtieSVG, height, width, zoomBehavior])

    const handleOpenFirstBarriersLevel = useCallback(() => {
        dispatch<IReduxActions['bowtieExpandedBarriers']>({
            type: 'SET_THREAT_EXPANDED_BARRIERS',
            payload: {
                newExpandedBarriers: threatsExpandedBarriers
                    .filter(e => e.barrierId.split('|').length === 1)
                    .map(e => {
                        e.escalations[0].x = initialThreat

                        return e
                    })
            }
        })

        dispatch<IReduxActions['bowtieExpandedBarriers']>({
            type: 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
            payload: {
                newExpandedBarriers: consequencesExpandedBarriers
                    .filter(e => e.barrierId.split('|').length === 1)
                    .map(e => {
                        e.escalations[0].x = initialConsequence

                        return e
                    })
            }
        })

        setOpenClose('openAll')
        setTimeout(() => setOpenClose('none'), 100)
    }, [consequencesExpandedBarriers, dispatch, threatsExpandedBarriers])

    const handleCloseAllBarriers = useCallback(() => {
        dispatch<IReduxActions['bowtieExpandedBarriers']>({
            type: 'SET_THREAT_EXPANDED_BARRIERS',
            payload: {
                newExpandedBarriers: threatsExpandedBarriers
                    .filter(e => e.barrierId.split('|').length === 1)
                    .map(e => {
                        e.escalations[0].x = initialThreat

                        return e
                    })
            }
        })

        dispatch<IReduxActions['bowtieExpandedBarriers']>({
            type: 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
            payload: {
                newExpandedBarriers: consequencesExpandedBarriers
                    .filter(e => e.barrierId.split('|').length === 1)
                    .map(e => {
                        e.escalations[0].x = initialConsequence

                        return e
                    })
            }
        })

        setOpenClose('closeAll')
        setTimeout(() => setOpenClose('none'), 100)
    }, [consequencesExpandedBarriers, dispatch, threatsExpandedBarriers])

    /** ------------------------------------ END ACTION BUTTONS FUNCTIONS ------------------------------------ **/

    // This function update the paths to make lines without warn color stay before the lines with warn color
    const updatePaths = (): void => {
        d3.selectAll<d3.BaseType, SVGPathElement>('path.bowtie-escalation-line').lower()
        d3.selectAll<d3.BaseType, SVGPathElement>('path.bowtie-escalation-line:not(.warn)').lower()
    }

    const getSearchMiddleY = (): number => {
        try {
            const height = getHeight(searchDataId.startsWith('thr') ? 'threats' : 'consequences') / 2

            return -height + (searchPosition.y ?? 0)
        } catch (error) {
            return 0
        }
    }

    const handleGetZoomLevel = useCallback(() => getZoomLevel(translation.k, customZoomLevels), [
        translation.k,
        customZoomLevels
    ])

    const initd3Listeners = useCallback(async () => {
        const svg = await d3PromiseSelect<SVGSVGElement, unknown>('#bowtie-svg')
        const zoomHandler = d3
            .zoom()
            .on('zoom', () => setTranslation({ ...d3.event.transform }))
            .scaleExtent([0.25, 2])

        zoomHandler(svg)
        svg.on('dblclick.zoom', null)

        svg.call(zoomHandler.transform, d3.zoomIdentity.translate(width / 2, height / 2 + 30).scale(1))

        setZoomBehavior(() => zoomHandler)
        setBowtieSVG(svg)
    }, [height, width])

    useEffect(() => {
        dispatch<IReduxActions['bowtieLanguage']>({
            type: 'SET_BOWTIE_LANGUAGE',
            payload: { newLanguage: locale ?? 'pt_br' }
        })
        dispatch<IReduxActions['mandatoryBarrier']>({
            type: 'SET_MANDATORY_BARRIERS',
            payload: { newBarrierIds: bowtieData.topEvent.dependentBarriers.map(b => b.id) }
        })

        initd3Listeners()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // This useEffect recalculate the Threat maximum X
    useEffect(() => {
        const allX = []
        threatsExpandedBarriers.forEach(e => e.escalations.forEach(es => allX.push(es.x)))
        allX.sort((a, b) => a - b)

        if (allX.length && allX[0] < initialThreat) {
            setThreatX(allX[0])
        } else {
            setThreatX(initialThreat)
        }

        updatePaths()
    }, [threatsExpandedBarriers]) // eslint-disable-line react-hooks/exhaustive-deps

    // This useEffect recalculate the Consequence maximum X
    useEffect(() => {
        const allX = []
        consequencesExpandedBarriers.forEach(e => e.escalations.forEach(es => allX.push(es.x)))
        allX.sort((a, b) => b - a)

        if (allX.length && allX[0] > initialConsequence) {
            setConsequenceX(allX[0])
        } else {
            setConsequenceX(initialConsequence)
        }

        updatePaths()
    }, [consequencesExpandedBarriers]) // eslint-disable-line react-hooks/exhaustive-deps

    // This useEffect is used to highlight searched barrier
    useEffect(() => {
        if (searchPosition && searchPosition.x !== null && searchPosition.y !== null) {
            setHighlightCircleOpacity(0.5)
            setHighlightInnerCircleRadius(0)
            setHighlightCircleRadius(0)

            setTimeout(() => setHighlightCircleRadius(2), 500)
            setTimeout(() => setHighlightInnerCircleRadius(2.1), 800)
            setTimeout(() => {
                setHighlightCircleOpacity(0)
                dispatch<IReduxActions['searchBarrier']>({ type: 'CLEAR_SEARCH_BARRIER' })
            }, 1000)

            bowtieSVG.call(
                zoomBehavior.transform,
                d3.zoomIdentity.translate(width / 2 - searchPosition.x, height / 2 - getSearchMiddleY())
            )
            zoomBehavior.scaleTo(bowtieSVG, 1.5)
        } else {
            setHighlightCircleOpacity(0)
            setHighlightCircleRadius(0)
            setHighlightInnerCircleRadius(0)
        }
    }, [searchPosition]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        handleFetchBarrierTypeIconsAndStoreIntoDictionary()
        handleFetchBarrierTypes()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const threatFill = (): string => {
        let threatId = ''

        bowtieData.topEvent.threats.map(threat => (threatId += threat.id + `;\n`))

        return threatId
    }

    const threatBarrierFill = (): string => {
        let threatBarrierId = ''

        bowtieData.topEvent.threats.map(threat =>
            threat.barriers.map(barrier => (threatBarrierId += barrier.id + `;\n`))
        )

        return threatBarrierId
    }

    const consequenceFill = (): string => {
        let consequenceId = ''

        bowtieData.topEvent.consequences.map(consequence => (consequenceId += consequence.id + `;\n`))

        return consequenceId
    }

    const consequenceBarrierFill = (): string => {
        let consequenceBarrierId = ''

        bowtieData.topEvent.consequences.map(consequence =>
            consequence.barriers.map(barrier => (consequenceBarrierId += barrier.id + `;\n`))
        )

        return consequenceBarrierId
    }

    return bowtieData ? (
        <>
            <BowTieStyledContainer>
                <ActionButtons
                    rig={bowtieData.asset.name}
                    bowtieId={bowtieData.id}
                    onCloseBowTie={onCloseBowtie}
                    zoomReset={handleResetZoom}
                    onCloseAll={handleCloseAllBarriers}
                    onOpenAll={handleOpenFirstBarriersLevel}
                    zoomOut={handleZoomOut}
                    zoomIn={handleZoomIn}
                    toggleSearchModal={handleOpenSearchModal}
                    toggleHelpModal={handleOpenHelpModal}
                    generateBowtiePDF={generateBowtiePDF}
                />
                <svg
                    id="bowtie-svg"
                    width={width}
                    height={height > 0 ? height : 0}
                    viewBox={`0 0 ${width} ${height > 0 ? height : 0}`}
                >
                    <defs>
                        <filter id="blur" x="-50" y="-50" width="200" height="200">
                            <feGaussianBlur result="blurOut" in="offOut" stdDeviation="5" />
                        </filter>
                    </defs>
                    {drawShadow()}
                    <g
                        id="bowtie-container"
                        transform={`translate(${translation.x} ${translation.y}) scale(${translation.k})`}
                    >
                        {/* threat side */}
                        <g transform={`translate(0, ${-getHeight('threats') / 2})`}>
                            {event.topEventView.threatsView.map((threatView, index) => {
                                const threat = threatView.threat

                                return (
                                    <ThreatAndConsequence
                                        key={threat.id}
                                        event={threatView}
                                        type="threat"
                                        dataId={`thr-${threat.id}`}
                                        threatConsequenceData={threat}
                                        middleY={getHeight('threats') / 2}
                                        x={threatX}
                                        y={getYPosition('threat', index)}
                                        rootXIndex={index}
                                        openClose={openClose}
                                        zoomLevel={handleGetZoomLevel()}
                                        barrierTypeIconMap={barrierTypeIconMap}
                                        bowtieMethodType={event.bowtie.methodType}
                                    />
                                )
                            })}
                        </g>
                        {/* consequence side */}
                        <g transform={`translate(0, ${-getHeight('consequences') / 2})`}>
                            {event.topEventView.consequencesView.map((consequenceView, index) => {
                                const consequence = consequenceView.consequence

                                return (
                                    <ThreatAndConsequence
                                        key={consequence.id}
                                        event={consequenceView}
                                        type="consequence"
                                        dataId={`cnq-${consequence.id}`}
                                        threatConsequenceData={consequence}
                                        middleY={getHeight('consequences') / 2}
                                        x={consequenceX}
                                        y={getYPosition('consequence', index)}
                                        rootXIndex={index}
                                        openClose={openClose}
                                        zoomLevel={handleGetZoomLevel()}
                                        barrierTypeIconMap={barrierTypeIconMap}
                                        bowtieMethodType={event.bowtie.methodType}
                                    />
                                )
                            })}
                        </g>
                        <MiddlePulse
                            event={event.topEventView}
                            title={bowtieData.topEvent.name}
                            alarpThresholds={alarpThresholds}
                            bowtieMethodType={event.bowtie.methodType}
                        />
                        {searchPosition && searchPosition.x !== null && searchPosition.y !== null && (
                            <>
                                <defs>
                                    <mask id="bigmask">
                                        <rect
                                            x={(searchPosition?.x ?? 0) - (barrierHeight * 5) / 2}
                                            y={getSearchMiddleY() - (barrierHeight * 5) / 2}
                                            width={barrierHeight * 5}
                                            height={barrierHeight * 5}
                                            fill="white"
                                        />
                                        <circle
                                            cx={searchPosition?.x ?? 0}
                                            cy={getSearchMiddleY()}
                                            r={barrierHeight * highlightInnerCircleRadius}
                                            filter="url(#blur)"
                                        />
                                    </mask>
                                </defs>
                                <circle
                                    cx={searchPosition?.x ?? 0}
                                    cy={getSearchMiddleY()}
                                    r={barrierHeight * highlightCircleRadius}
                                    mask="url(#bigmask)"
                                    opacity={highlightCircleOpacity}
                                    fill="#DD0"
                                    filter="url(#blur)"
                                />
                            </>
                        )}
                    </g>

                    <TopBar
                        bowtieCode={bowtieData.bowtieCode}
                        hazard={bowtieData.topEvent.hazard.name}
                        relativeMiddleX={translation.x}
                        relativeMiddleY={translation.y}
                        widgetWidth={width}
                        consequencesX={consequenceX + translation.x}
                        threatsX={threatX - translation.x}
                        scale={translation.k}
                    />
                </svg>
                <BarrierDetailsModal
                    barrierTypeIconMap={barrierTypeIconMap}
                    dependentBarriers={bowtieData.topEvent.dependentBarriers}
                    bowtieMethodType={event.bowtie.methodType}
                />
                <HelpModal
                    show={openHelpModal}
                    onClose={handleCloseHelpModal}
                    extraInfo={extraInfo}
                    barrierTypeIconMap={barrierTypeIconMap}
                    barrierTypes={barrierTypes}
                />
                <SearchModal
                    show={openSearchModal}
                    consequences={bowtieData.topEvent.consequences}
                    threats={bowtieData.topEvent.threats}
                    onClose={() => setOpenSearchModal(false)}
                />
            </BowTieStyledContainer>
            <PdfTableContainer id="a4-export-bowtie-pdf">
                <CustomList
                    className="bowtie-export-pdf"
                    label="Bowties"
                    list={[
                        {
                            asset: bowtieData.asset.name,
                            bowtieCode: bowtieData.bowtieCode,
                            hazard: bowtieData.topEvent.hazard.name,
                            topEvent: bowtieData.topEvent.name,
                            operation: bowtieData.topEvent.hazard.operation.description,
                            threat_ids: threatFill(),
                            threat_barrier_ids: threatBarrierFill(),
                            consequence_id: consequenceFill(),
                            consequence_barrier_ids: consequenceBarrierFill()
                        }
                    ]}
                    columns={[
                        { title: 'RESOURCE', listRef: 'asset', sortable: true },
                        { title: 'BOWTIE CODE', listRef: 'bowtieCode', sortable: true },
                        { title: 'HAZARD', listRef: 'hazard', sortable: true },
                        { title: 'TOP EVENT', listRef: 'topEvent', sortable: true },
                        { title: 'OPERATION', listRef: 'operation', sortable: true },
                        { title: 'THREAT IDS', listRef: 'threat_ids', sortable: true },
                        { title: 'THREAT BARRIER IDS', listRef: 'threat_barrier_ids', sortable: true },
                        { title: 'CONSEQUENCE IDS', listRef: 'consequence_id', sortable: true },
                        { title: 'CONSEQUENCE BARRIER IDS', listRef: 'consequence_barrier_ids', sortable: true }
                    ]}
                    sortDirection={'ASC'}
                    sortField={''}
                    onSortClick={null}
                    tableProps={{ style: { width: '100%' } }}
                    pagination={{
                        type: 'live',
                        totalItems: 1,
                        showingItems: 1,
                        onPerPageChange: null
                    }}
                />
            </PdfTableContainer>
        </>
    ) : (
        <></>
    )
}
